/**
 * useSessionList — server-backed session index for Home chat history rail.
 */
import * as React from "react";
import { deleteSession as deleteSessionCall, listSessions, updateSession, type Session } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";
import { apiErrorMessage } from "@/lib/api-error";

export type SessionSummary = Session;

export function useSessionList(activeSessionId: string | null) {
  const auth = useAuth();
  const client = useApiClient();
  const [sessions, setSessions] = React.useState<SessionSummary[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  // The client is rebuilt when the token getter changes; a ref keeps the
  // callbacks (and the poll below) stable across that.
  const clientRef = React.useRef(client);
  clientRef.current = client;

  const refetch = React.useCallback(async (): Promise<SessionSummary[]> => {
    const { data: rows } = await listSessions({ client: clientRef.current, throwOnError: true });
    setSessions(rows);
    setError(null);
    return rows;
  }, []);

  const renameSession = React.useCallback(
    async (id: string, title: string): Promise<SessionSummary> => {
      const { data: row } = await updateSession({
        client: clientRef.current,
        path: { session_id: id },
        body: { title },
        throwOnError: true,
      });
      setSessions((prev) => prev.map((s) => (s.id === id ? { ...s, ...row } : s)));
      return row;
    },
    [],
  );

  const deleteSession = React.useCallback(async (id: string): Promise<void> => {
    const { response } = await deleteSessionCall({ client: clientRef.current, path: { session_id: id } });
    // Already gone is fine: the goal state is "not in the list".
    if (!response.ok && response.status !== 404) throw new Error(`delete failed: ${response.status}`);
    setSessions((prev) => prev.filter((s) => s.id !== id));
  }, []);

  React.useEffect(() => {
    if (!auth.ready || !auth.user) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        await refetch();
      } catch (e) {
        if (!cancelled) setError(apiErrorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.ready, auth.user, refetch]);

  // Light poll while tab visible — picks up messages from other devices.
  React.useEffect(() => {
    if (!auth.ready || !auth.user) return;
    const tick = (): void => {
      if (document.visibilityState === "visible") void refetch().catch(() => {});
    };
    const id = window.setInterval(tick, 15_000);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [auth.ready, auth.user, refetch, activeSessionId]);

  const recent = React.useMemo(() => {
    const visible = sessions.filter(
      (s) =>
        s.message_count > 0 ||
        s.last_message_preview ||
        s.id === activeSessionId,
    );
    const base = visible.length > 0 ? visible : sessions;
    return base.slice(0, 8);
  }, [sessions, activeSessionId]);

  const older = React.useMemo(() => {
    const visible = sessions.filter(
      (s) =>
        s.message_count > 0 ||
        s.last_message_preview ||
        s.id === activeSessionId,
    );
    const base = visible.length > 0 ? visible : sessions;
    return base.slice(8);
  }, [sessions, activeSessionId]);

  return { sessions, recent, older, loading, error, refetch, renameSession, deleteSession };
}
